import { Skeleton, List, ListItem, ListItemButton } from "@mui/material";
import type { KeyboardEvent } from "react";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import type { ISpaceAuthLookupResponse } from "@/api/space/dtos/authenticator.dto";
import { MIN_CARACTERES_LOOKUP_USUARIO } from "@/configs/system";

// --------------------------------- TYPES ---------------------------------
type DialogoSelecaoUsuarioResultadosProps = {
	readonly dados: {
		readonly atingiuTamanhoMinimo: boolean;
		readonly usuarios: readonly ISpaceAuthLookupResponse[];
		readonly indiceAtivo: number;
	};
	readonly carregando: boolean;
	readonly erro: boolean;
	readonly acoes: {
		readonly aoRegistrarReferencia: (indice: number, elemento: HTMLDivElement | null) => void;
		readonly aoAtivar: (indice: number) => void;
		readonly aoTeclaUsuario: (evento: KeyboardEvent<HTMLElement>, indice: number) => void;
		readonly aoSelecionar: (usuario: ISpaceAuthLookupResponse) => void;
	};
};

// -------------------------------- HELPERS --------------------------------
function obterNomeUsuario(usuario: ISpaceAuthLookupResponse): string {
	return usuario.nome?.trim() || `Usuário ${usuario.id_cad_pessoa ?? "sem identificação"}`;
}

// ------------------------------- COMPONENTS ------------------------------
function EstadoLista({ icone, titulo, descricao }: Readonly<{ icone: string; titulo: string; descricao: string }>) {
	return (
		<div
			className="flex min-h-48 flex-col items-center justify-center gap-3 px-5 py-8 text-center"
			role="status"
			aria-live="polite"
		>
			<div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
				<FuseSvgIcon size={24}>{icone}</FuseSvgIcon>
			</div>
			<div className="max-w-sm">
				<h2 className="text-sm font-bold text-foreground">{titulo}</h2>
				<p className="mt-1 text-sm leading-5 text-muted-foreground">{descricao}</p>
			</div>
		</div>
	);
}

// ------------------------------- COMPONENT -------------------------------
export default function DialogoSelecaoUsuarioResultados({
	dados: { atingiuTamanhoMinimo, usuarios, indiceAtivo },
	carregando,
	erro,
	acoes: { aoRegistrarReferencia, aoAtivar, aoTeclaUsuario, aoSelecionar },
}: Readonly<DialogoSelecaoUsuarioResultadosProps>) {
	if (!atingiuTamanhoMinimo)
		return (
			<EstadoLista
				icone="lucide:text-cursor-input"
				titulo="Continue digitando"
				descricao={`Informe pelo menos ${MIN_CARACTERES_LOOKUP_USUARIO} caracteres para pesquisar.`}
			/>
		);

	if (carregando)
		return (
			<div className="flex min-h-48 flex-col gap-3 px-4 py-3" aria-busy="true" role="status" aria-live="polite">
				<span className="sr-only">Pesquisando usuários…</span>
				{["primeiro", "segundo", "terceiro"].map((chave) => (
					<div key={chave} className="flex items-center gap-3">
						<Skeleton variant="circular" width={36} height={36} />
						<div className="flex-1">
							<Skeleton width="65%" height={20} />
							<Skeleton width="35%" height={16} />
						</div>
					</div>
				))}
			</div>
		);

	if (erro)
		return (
			<EstadoLista
				icone="lucide:triangle-alert"
				titulo="Não foi possível buscar os usuários"
				descricao="Verifique sua conexão e altere a pesquisa para tentar novamente."
			/>
		);

	if (usuarios.length === 0)
		return (
			<EstadoLista
				icone="lucide:user-round-x"
				titulo="Nenhum usuário encontrado"
				descricao="Revise o nome ou ID informado e tente novamente."
			/>
		);

	return (
		<List disablePadding aria-label="Usuários encontrados">
			{usuarios.map((usuario, indice) => {
				const nomeUsuario = obterNomeUsuario(usuario);
				const chave = usuario.id_cad_pessoa ?? `${nomeUsuario}-${indice}`;
				const estaAtivo = indice === indiceAtivo;

				return (
					<ListItem key={chave} disablePadding divider>
						<ListItemButton
							ref={(elemento) => {
								aoRegistrarReferencia(indice, elemento);
							}}
							className="flex items-center gap-3 px-4 py-3 [&.Mui-selected]:bg-primary/10"
							selected={estaAtivo}
							tabIndex={estaAtivo ? 0 : -1}
							onFocus={() => aoAtivar(indice)}
							onMouseEnter={() => aoAtivar(indice)}
							onKeyDown={(evento) => aoTeclaUsuario(evento, indice)}
							onClick={() => aoSelecionar(usuario)}
							aria-label={`Selecionar ${nomeUsuario}`}
							aria-current={estaAtivo ? "true" : undefined}
						>
							<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
								<FuseSvgIcon size={18}>lucide:user-round</FuseSvgIcon>
							</div>
							<div className="min-w-0 flex-1">
								<div className="truncate text-sm font-bold text-foreground">{nomeUsuario}</div>
								<div className="text-xs text-muted-foreground">
									ID: {usuario.id_cad_pessoa ?? "não informado"}
								</div>
							</div>
							<FuseSvgIcon className="shrink-0 text-muted-foreground" size={18}>
								lucide:chevron-right
							</FuseSvgIcon>
						</ListItemButton>
					</ListItem>
				);
			})}
		</List>
	);
}
